import React, { useEffect } from "react";
import { Accordion, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlay, Loader } from "react-overlay-loader";

import { fetchMessageSecurityQuestionSettings } from "src/actionCreators/adminSettingsActionCreators";
import {
    addMessageSecurityQuestions,
    fetchMessageSecurityQuestions,
} from "src/actionCreators/securityQuestionCreators";
import AuthMethodHeader from "src/components/Common/Navigation/Header/AuthMethodHeader";
import { AuthMethodDialogConstants } from "src/Core/Common/Constants";
import { SettingMode } from "src/Core/Common/Enums";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";

import { ApplicationState } from "src/store";
import AddSecurityQuestion from "./AddSecurityQuestion";
import HelpTextLabel from "./HelpTextLabel";
import RecipientQuestionAnswerRow from "./RecipientQuestionAnswerRow";
import { IRecipientModel } from "src/Core/ViewModels/Message/RecipientModel";
import { QnAActions, initialRecipientQuestionAnswer } from "src/reducers/recipientQuestionAnswerReducer";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";

const QuestionAnswerAuthMethod: React.FC = () => {
    const isUserAllowedToAddSecurityQuestion = useSelector(
        (state: ApplicationState) => state.adminSettingsState.messageSecuritySettings.isUserAllowedToAddSecurityQuestion
    );
    const securityQuestionState = useSelector((state: ApplicationState) => state.securityQuestionState);
    const recipients = useSelector((state: ApplicationState) => state.recipients);
    const dispatch = useDispatch();

    useEffect(() => {
        handleReloadMessageSecurityQuestions();
        dispatch(fetchMessageSecurityQuestionSettings());
    }, []);

    const handleAddSecurityQuestion = (
        securityQuestion: IMessageSecurityQuestion,
        isUserSecurityQuestion: boolean,
        callback?: any
    ) => {
        const securityQuestionPayload: IMessageSecurityQuestion = {
            ...securityQuestion,
            question: securityQuestion.question.trim(),
            questionTitle: securityQuestion.questionTitle.trim()
        };
        dispatch(addMessageSecurityQuestions(securityQuestionPayload, isUserSecurityQuestion, callback));
    };
    const handleReloadMessageSecurityQuestions = () => dispatch(fetchMessageSecurityQuestions(SettingMode.BOTH));

    const handleAddRow = () => {
        dispatch({
            type: QnAActions.ADD,
            payload: initialRecipientQuestionAnswer,
        });
        logger.trackEvent({
            name: `${appInsightsData.Compose.QnAAuthMethod.pageTitle} - ${appInsightsData.Compose.Operation.RecipientAddedInQA}`,
            properties: {
                page: appInsightsData.Compose.QnAAuthMethod.pageTitle,
            },
        });
    };

    const handleRemoveRow = (recipient: IRecipientModel) => {
        dispatch({
            type: QnAActions.REMOVE,
            payload: { emailAddress: recipient.emailAddress, index: recipient.index },
        });
        logger.trackEvent({
            name: `${appInsightsData.Compose.QnAAuthMethod.pageTitle} - ${appInsightsData.Compose.Operation.RecipientRemovedInQA}`,
            properties: {
                page: appInsightsData.Compose.QnAAuthMethod.pageTitle,
                recipientIndex: recipient.index,
                emailAddress: recipient.emailAddress,
            },
        });
    };

    const handleEditRow = (recipient: IRecipientModel) => {
        dispatch({
            type: QnAActions.EDIT,
            payload: { index: recipient.index },
        });
        logger.trackEvent({
            name: `${appInsightsData.Compose.QnAAuthMethod.pageTitle} - ${appInsightsData.Compose.Operation.RecipientEditedInQA}`,
            properties: {
                page: appInsightsData.Compose.QnAAuthMethod.pageTitle,
                recipientIndex: recipient.index,
            },
        });
    };

    const handleSaveRow = (recipient: IRecipientModel, data: IRecipientModel) => {
        dispatch({
            type: QnAActions.SAVE,
            payload: { data, index: recipient.index },
        });
    };

    const handleValidateDuplicateRecipient = (email: string, index?: number): boolean => {
        return recipients.find(x => x.emailAddress === email && x.index !== index) ? true : false;
    };

    return (
        <>
            <AuthMethodHeader title={AuthMethodDialogConstants.QNA_HEADER_TITLE} />
            <HelpTextLabel title={AuthMethodDialogConstants.QNA_METHOD_HELP_TEXT} />

            <LoadingOverlay style={{ height: "100%" }}>
                <div className="compose__qna_wrapper">
                    {isUserAllowedToAddSecurityQuestion && (
                        <AddSecurityQuestion
                            questions={securityQuestionState.securityQuestions}
                            addSecurityQuestion={handleAddSecurityQuestion}
                            reloadMessageSecurityQuestions={handleReloadMessageSecurityQuestions}
                        />
                    )}
                    <Accordion defaultActiveKey="0" className="compose__qna" defaultChecked={false}>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>Set Questions &amp; Answers</Accordion.Header>
                            <Accordion.Body>
                                <div className="qna__table-wrapper">
                                    {recipients.map((item, i, arr) => {
                                        return (
                                            <RecipientQuestionAnswerRow
                                                questions={securityQuestionState.securityQuestions.data}
                                                data={item}
                                                isLastRow={arr.length === 1}
                                                totalRows={arr.length}
                                                key={"recipient_" + i}
                                                onAddRow={handleAddRow}
                                                onRemoveRow={handleRemoveRow}
                                                onEditRow={handleEditRow}
                                                onSaveRow={handleSaveRow}
                                                validateDuplicateRecipient={handleValidateDuplicateRecipient}
                                            />
                                        );
                                    })}
                                    <Row>
                                        <Col xs={3}>
                                            <a onClick={handleAddRow} className="qna__add-row">
                                                Add New Recipient&nbsp;
                                                <span className="fas fa-user-plus"></span>
                                            </a>
                                        </Col>
                                    </Row>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                <Loader
                    loading={
                        securityQuestionState.securityQuestions.isLoading ||
                        securityQuestionState.securityQuestions.data.length == 0
                    }
                />
            </LoadingOverlay>
        </>
    );
};

export default QuestionAnswerAuthMethod;
